import React from 'react';
import { artistInfo } from '../mock';
import { MapPin, Instagram, Music } from 'lucide-react';
import { useMusic } from '../contexts/MusicContext';

const AboutSection = () => {
  // Use shared music context — no separate API call needed
  const musicData = useMusic();
  const profile = musicData.soundcloud_profile || {};

  const soundcloudData = {
    followers: profile.followers_count !== undefined
      ? profile.followers_count.toString()
      : artistInfo.soundcloudFollowers,
    totalTracks: profile.track_count
      ? profile.track_count.toString()
      : (musicData.soundcloud_tracks?.length || artistInfo.totalTracks).toString()
  };
  return (
    <section className="py-32 bg-black relative" id="about">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-[7.6923%]">
        <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Left - Text Content */}
          <div className="space-y-8">
            <div>
              <p id="about-section-tab" className="section-tab">About</p>
              <h2 id="about-section-title" className="section-title">About JLEE</h2>
              <p className="body-medium text-white/85 leading-relaxed mb-4">
                JLEE has been producing music as a professional DJ for years, performing at venues across Canada. Specializing in dubstep and bass music, JLEE brings high-energy electronic sets to clubs, festivals, and events nationwide.
              </p>
              <p className="body-medium text-white/85 leading-relaxed mb-4">
                A versatile performer capable of diverse electronic styles — from high-energy dubstep sets to chill, ambient vibes. Experienced in both live performance and studio production, JLEE creates music that resonates with electronic music enthusiasts.
              </p>
              <p className="body-medium text-white/85 leading-relaxed mb-4">
                Based in Kelowna, BC, with releases available on SoundCloud and Apple Music.
              </p>
            </div>

            {/* Info Cards */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 box-card">
                <MapPin className="text-brand-turquoise flex-shrink-0" size={20} />
                <div>
                  <p className="body-small text-white/70">Location</p>
                  <p className="body-medium text-white">Kelowna, BC</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 box-card">
                <Instagram className="text-brand-turquoise flex-shrink-0" size={20} />
                <div>
                  <p className="body-small text-white/70">Follow</p>
                  <a href={`https://instagram.com/${artistInfo.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="body-medium text-white hover:text-brand-turquoise transition-colors">
                    {artistInfo.instagram}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 box-card">
                <Music className="text-brand-turquoise flex-shrink-0" size={20} />
                <div>
                  <p className="body-small text-white/70">SoundCloud</p>
                  <a href={artistInfo.soundcloudUrl} target="_blank" rel="noopener noreferrer" className="body-medium text-white hover:text-brand-turquoise transition-colors">
                    {soundcloudData.totalTracks} tracks • {soundcloudData.followers} followers
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Logo and Background */}
          <div className="relative group h-[400px] md:h-[600px] bg-black overflow-hidden mt-8 md:mt-0">
            {/* Abstract Background with Grid Pattern */}
            <div className="absolute inset-0">
              <div className="w-full h-full bg-gradient-to-br from-black via-[#0a0a0a] to-black opacity-80" />
              {/* Neon Grid Effect */}
              <div className="absolute inset-0 grid-overlay opacity-20" />
              {/* Animated Gradient Orbs */}
              <div className="absolute top-20 left-20 w-64 h-64 bg-[var(--brand-turquoise)]/20 rounded-full blur-3xl animate-pulse" />
              <div className="absolute bottom-20 right-20 w-80 h-80 bg-[var(--brand-turquoise)]/10 rounded-full blur-3xl animate-pulse animate-pulse-delay-1s" />
            </div>

            {/* Logo on Top */}
            <div className="relative z-10 flex flex-col items-center justify-center h-full p-8">
              <div className="mb-8">
                <img
                  src={artistInfo.profileImage}
                  alt="JLEE logo"
                  className="w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 object-contain"
                  loading="eager"
                  onError={(e) => {
                    // Fallback if image fails to load
                    e.target.src = 'https://via.placeholder.com/256?text=JLEE';
                  }}
                />
              </div>
              <div className="text-center">
                <p id="about-logo-title" className="subsection-title mb-2">JLEE</p>
                <p className="body-large text-brand-turquoise mb-4">Electronic Music Producer</p>
                <p className="body-medium text-white/85">Kelowna, BC</p>
              </div>
            </div>

            {/* Glow Effect */}
            <div className="absolute inset-0 bg-[var(--brand-turquoise)]/5 mix-blend-overlay group-hover:bg-[var(--brand-turquoise)]/10 transition-all duration-500" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;